import React from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import DashboardService from "../../Services/DashboardService";
import { Utils } from "../../Utilities/Utils";
import { EmptyIcon } from "../../Common/FullPageSpinner";

interface IInvoiceCardProps {
  practiceId: number | null;
  fromDate: string;
  toDate: string;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: "#fff",
        border: "1px solid rgba(224,123,42,0.18)",
        borderRadius: 10,
        padding: "10px 14px",
        boxShadow: "0 4px 20px rgba(15,27,60,0.12)",
        fontFamily: "Inter, sans-serif",
      }}>
        <div style={{ fontSize: 11, color: "#8fa3b8", fontWeight: 500, marginBottom: 5 }}>{label}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#e07b2a" }} />
          <span style={{ fontSize: 15, fontWeight: 800, color: "#e07b2a" }}>
            £{Utils.getFormattedNumber(payload[0].value)}
          </span>
        </div>
      </div>
    );
  }
  return null;
};

const InvoiceCard = ({ practiceId, fromDate, toDate }: IInvoiceCardProps) => {
  const [chartData, setChartData] = React.useState<any[]>([]);
  const [loader, setLoader] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (!practiceId) {
      setChartData([]);
      return;
    }
    setLoader(true);
    DashboardService.getInvoiceDetails(practiceId, fromDate, toDate)
      .then((response) => {
        if (response?.status && response?.result) {
          const xData = Object.keys(response.result);
          const yData = Object.values(response.result) as number[];
          for (var i = 0; i < xData.length; i++) {
            xData[i] = xData[i].charAt(0).toUpperCase() + xData[i].slice(1);
          }
          setChartData(xData.map((key, i) => ({ name: key, value: yData[i] })));
        } else {
          setChartData([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching chart data:", error);
        setChartData([]);
      })
      .finally(() => {
        setLoader(false);
      });
  }, [practiceId, fromDate, toDate]);

  return (
    <Spin
      spinning={loader}
      indicator={<LoadingOutlined style={{ fontSize: 30, color: "#e07b2a" }} spin />}
    >
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={310}>
          <LineChart data={chartData} margin={{ top: 8, right: 32, left: 16, bottom: 8 }}>
            <defs>
              <linearGradient id="invoiceLineGradient" x1="1" y1="0" x2="0" y2="0">
                <stop offset="0%" stopColor="#f5a56c" />
                <stop offset="100%" stopColor="#e07b2a" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="4 4" stroke="#f0f4ff" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fill: "#8fa3b8", fontSize: 11, fontFamily: "Inter, sans-serif" }}
              tickLine={false}
              axisLine={{ stroke: "#e8eef8" }}
            />
            <YAxis
              tick={{ fill: "#8fa3b8", fontSize: 11, fontFamily: "Inter, sans-serif" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => "£" + Utils.getFormattedNumber(v)}
              width={80}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "rgba(224,123,42,0.08)", strokeWidth: 2 }} />
            <Line
              type="monotone"
              dataKey="value"
              stroke="url(#invoiceLineGradient)"
              strokeWidth={3}
              dot={{ r: 5, fill: "#e07b2a", stroke: "#fff", strokeWidth: 2 }}
              activeDot={{ r: 6, fill: "#e07b2a", stroke: "#fff", strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 80, gap: 8 }}>
          <EmptyIcon />
        </div>
      )}
    </Spin>
  );
};

export default InvoiceCard;